
import { isEqual } from 'lodash';
import { Interval } from './Interval';
import * as sensorDataAnalysis from '../utils/sensorDataAnalysis';
import { Workout, Rep } from './Workout';

interface Activity {
    activityType: ActivityType,
    paused: boolean,
    calibrated:boolean,
    beforePausedState: string,
    intervals: Array<Interval>,
    intervalStart: number,
    liveGraphData: { x: number, y: number },
    liveGraphDataArray: Array<{ x: number, y: number }>,
    currentRepIndex: number,
    currentRep: Rep,
    userWeight: number,
    userStrength:number,
    addedWeights:number,
    pulledWeight:any,
    lastPulledWeight:any,
    workout: Workout,

    updateCurrentRep(),
    updateLiveGraphData(time:number, strength:number),
    updateIntervals(),
    resetGraph(),
    updatePulledWeigth(forcePlateData:number),

    start(),
    pause(activityState:string),
    restart(),
    stop(),
    finish(sensorData?: [number, number][]),
    reset(),
    detectUserAction(pulledWeight: number | string)
}
interface ActivityType{
    title: string,
    type: string,
    selectedWorkout: string,
    notes: string,
}
class Activity implements Activity {
  constructor(activityTypeParam: ActivityType, workout: Workout) {
    this.activityType = activityTypeParam;
    this.paused = false;
    this.calibrated = false;
    this.intervals = new Array();
    this.liveGraphData = { x: 0, y: 0 };
    this.liveGraphDataArray = [];
    this.beforePausedState = null;
    this.intervalStart = null;
    this.currentRepIndex = 0;
    this.userWeight = 0;
    this.workout = new Workout(workout);
    this.pulledWeight = 0;
    this.lastPulledWeight = 0;
    this.addedWeights = 0;
    this.userStrength = 0;
    this.currentRep = new Rep();

    this.updateCurrentRep = () => {
      this.currentRepIndex += 1;
      this.currentRep = this.workout.getRepByIndex(this.currentRepIndex);
      this.pulledWeight = null;
      // Already calculated for all reps during initialization or weight updates
      this.liveGraphDataArray = [];
    };

    this.resetGraph = () => {
      this.liveGraphDataArray = [];
      this.liveGraphData = { x: 0, y: 0 };
    };

    this.updateIntervals = () => {
      const index = sensorDataAnalysis._weightDataArray.length - 1;
      if (this.intervalStart != null) {
        // Close the current interval
        const repIndex = this.currentRepIndex == 0 ? 0 : this.currentRepIndex - 1;
        const currentFingers = this.workout.getRepByIndex(repIndex).fingers;
        const currentGrip = this.workout.getRepByIndex(repIndex).grip;
        console.log(`Activity: Closing interval for rep ${repIndex}, start: ${this.intervalStart}, end: ${index}`);
        this.intervals.push(new Interval({
          start: this.intervalStart, 
          end: index, 
          fingers: currentFingers, 
          grip: currentGrip,
        }));
        this.intervalStart = null;
        this.liveGraphDataArray = [];
      }
      
      // Start a new interval if we're moving to a hang rep (intensity > 0)
      const nextRepIndex = this.currentRepIndex;
      const nextRep = this.workout.getRepByIndex(nextRepIndex);
      if (nextRep && nextRep.intensity !== 0 && this.intervalStart === null) {
        // Starting a new hang interval
        this.intervalStart = index;
        console.log(`Activity: Starting new interval for rep ${nextRepIndex}, start index: ${index}`);
      }
    };

    this.updateLiveGraphData = (time:number, strength:number) => {
      this.liveGraphData = {
        x: time,
        y: strength,
      };
      this.liveGraphDataArray.push(this.liveGraphData);
      return this.liveGraphData;
    };
    this.detectUserAction = (pulledWeight: number | string) => {
      const intervThreshold = 3; // [kg]
      
      // Handle special string values
      if (pulledWeight === 'MAX' || pulledWeight === 'Out') {
        return 'rest'; // MAX and Out indicate low/no force, so user is resting
      }
      
      // Handle numeric values
      const numericWeight = typeof pulledWeight === 'number' ? pulledWeight : 0;
      const result = numericWeight > intervThreshold ? 'hang' : 'rest';
      
      return result;
    };

    this.start = () => {
      this.paused = false;
      return this.beforePausedState ? this.beforePausedState : 'start';
    };
    this.pause = (activityState:string) => {
      this.paused = true;
      this.beforePausedState = activityState;
    };
    this.reset = () => {
      this.paused = false;
      this.intervals = new Array();
      this.liveGraphData = { x: 0, y: 0 };
      this.liveGraphDataArray = [];
      this.beforePausedState = null;
      this.intervalStart = null;
      this.currentRepIndex = 0;
      this.userWeight = 0;
      this.currentRep = { ...this.workout.getRepByIndex(0) };
      this.calculateAllExpectedStrengths();
    };

    this.finish = async (sensorData?: [number, number][]): Promise<any> => {
      // Close the current interval if one is in progress
      this.updateIntervals();
      
      // If there's still an intervalStart set, close it manually (user finished during a hang)
      if (this.intervalStart != null) {
        const index = sensorDataAnalysis._weightDataArray.length - 1;
        const repIndex = this.currentRepIndex == 0 ? 0 : this.currentRepIndex - 1;
        const currentFingers = this.workout.getRepByIndex(repIndex).fingers;
        this.intervals.push(new Interval({
          start: this.intervalStart, 
          end: index, 
          fingers: currentFingers, 
          grip: this.workout.getRepByIndex(repIndex).grip,
        }));
        this.intervalStart = null;
      }
      
      console.log('Activity: finish() - Total intervals collected:', this.intervals.length);
      console.log('Activity: finish() - Intervals:', this.intervals);
      console.log('Activity: finish() - Interval details:', this.intervals.map((iv: any) => ({
        start: iv.start,
        end: iv.end,
        stop: iv.stop,
        fingers: iv.fingers,
        grip: iv.grip
      })));
      console.log('Activity: finish() - Current rep index:', this.currentRepIndex);
      console.log('Activity: finish() - Interval start:', this.intervalStart);
      console.log('Activity: finish() - Data array length:', sensorDataAnalysis._weightDataArray.length);
      
      // Convert Interval class instances to plain objects for JSON serialization
      const intervalsAsPlainObjects = this.intervals.map((iv: any) => ({
        start: iv.start,
        end: iv.end,
        fingers: iv.fingers,
        grip: iv.grip,
        expectedStrength: iv.expectedStrength
      }));
      console.log('Activity: finish() - Intervals as plain objects:', intervalsAsPlainObjects);
      
      // Use provided sensor data or fall back to sensorDataAnalysis
      if (sensorData && sensorData.length > 0) {
        // Convert Redux sensor data to the format expected by onEnd
        const csvData = sensorData.map(([force, time]) => `${force},${time}`).join('\n');
        
        // Create archive object directly
        const now = new Date().toISOString();
        const archive = {
          "type": this.activityType.selectedWorkout ? this.activityType.selectedWorkout : "raw", 
          "subtype": this.activityType.type ? this.activityType.type : "none",
          "date": now ? now : "unknown time", 
          "title": this.activityType.title ? this.activityType.title : "Free hanging", 
          "tag": `${this.activityType.notes}`,
          "data": csvData,
          "userWeight": this.userWeight ? this.userWeight : 150, 
          "addedWeight": this.addedWeights,
          "notes": `${this.activityType.notes}`,
          "intervals": intervalsAsPlainObjects
        };
        
        console.log('Activity: finish() - Archive intervals count:', archive.intervals.length);
        this.reset();
        return archive;
      } else {
        // Fall back to sensorDataAnalysis if no Redux data provided
        // Convert Interval class instances to plain objects for JSON serialization
        return await sensorDataAnalysis.onEnd(this.userWeight, this.activityType, this.addedWeights, intervalsAsPlainObjects)
          .then((response) => {
            console.log('Activity: finish() - sensorDataAnalysis.onEnd returned intervals count:', response.intervals?.length || 0);
            this.reset();
            return response;
          });
      }
    };

    this.updatePulledWeigth = (forcePlateData:number): number => {
      let weight:any = Math.round(10 * (this.userWeight + this.addedWeights - forcePlateData)) / 10;
      if (this.userWeight + this.addedWeights - forcePlateData < 0.15) weight = 0;
      else if (forcePlateData < 0.15) weight = 'Out';
      this.lastPulledWeight = this.pulledWeight;
      this.pulledWeight = weight;

      return this.pulledWeight;
    };

    // Calculate initial expected strengths
    this.calculateAllExpectedStrengths();
  }

  setUserStrength(value: number) {
    this.userStrength = value !== null ? value : 60;
    this.calculateAllExpectedStrengths();
  }

  updateCalibrationData(userWeight: number, addedWeights: number) {
    this.userWeight = userWeight;
    this.addedWeights = addedWeights;
    this.calculateAllExpectedStrengths();
  }

  calculateAllExpectedStrengths() {
    console.log('Activity: Calculating expected strength for all reps');
    if (this.workout && this.workout.content && this.workout.content.reps) {
      this.workout.content.reps.forEach(rep => {
        this.calculateExpectedStrengthForRep(rep);
      });
      // also update currentRep reference
      this.currentRep = this.workout.getRepByIndex(this.currentRepIndex);
    }
  }

  calculateExpectedStrengthForRep(rep: Rep) {
    if (!this.workout) return;
    const intensityType = (this.workout.intensityType || '').toLowerCase();
    const { intensity } = rep;
    let maxValue = 0;
    
    // Ensure we have a default user strength if it's missing or zero
    const strength = this.userStrength > 0 ? this.userStrength : 60;

    if (intensity !== 0) {
      if (intensityType === 'strengthrelative') {
        const numberOfHands = isEqual(rep.fingers, [[1, 1, 1, 1, 0], [0, 1, 1, 1, 1]]) ? 2 : 1;
        maxValue = (intensity / 100) * strength * numberOfHands;
      } else if (intensityType === 'weightrelative') {
        maxValue = (intensity / 100) * (this.userWeight || 70);
      } else if (intensityType === 'absolute') {
        maxValue = intensity;
      }
    }
    rep.expectedStrength = maxValue;
  }

  calculateExpectedStrength() {
    this.calculateExpectedStrengthForRep(this.currentRep);
  }
}

export default Activity;
